import type { WorldSlug } from '@vrlearning/lesson-schema';
import data from '../../../worlds/manifest.json';

/**
 * The worlds' files as the import recorded them (`apps/headset/worlds/manifest.json`,
 * ADR-032): every file by the sha256 it is stored under, what it is and what
 * it measured. The headset needs nothing else to know what a world costs,
 * where its sun is, or what colour a surface falls back to.
 */

export type ManifestFile = {
  readonly id: string;
  readonly asset: string;
  readonly kind: 'hdri' | 'texture' | 'gltf' | 'bin' | 'audio';
  /** `<sha256>.<ext>`: its name in the bucket and under `/worlds/`. */
  readonly stored: string;
  readonly bytes: number;
  /** Fetched by headsets; false for a source a derived file replaces. */
  readonly served: boolean;
  readonly licence: string;
  readonly worlds: readonly string[];
  readonly derivedFrom?: string;
  readonly width?: number;
  readonly height?: number;
  readonly averageColour?: string;
  readonly triangles?: number;
  /** A plant simplified at import: the source's triangles. */
  readonly simplifiedFrom?: number;
  /** A set's parts, planted one at a time: name, triangles, standing size in metres. */
  readonly variants?: readonly {
    readonly name: string;
    readonly triangles: number;
    readonly size: readonly number[];
  }[];
  readonly primitives?: number;
  readonly materials?: number;
  readonly bounds?: { readonly min: readonly number[]; readonly max: readonly number[] };
  /** A served glTF's buffers and textures, by stored name. */
  readonly requires?: readonly string[];
  readonly sun?: {
    readonly direction: readonly [number, number, number];
    readonly azimuth: number;
    readonly elevation: number;
    readonly linear: readonly number[];
  };
  readonly horizon?: string;
  readonly horizonLinear?: readonly number[];
  readonly zenith?: string;
  readonly zenithLinear?: readonly number[];
  /** A sound (world audio, ADR-034): Freesound's, its recordist, its page. */
  readonly provider?: string;
  readonly sound?: string;
  readonly user?: string;
  readonly page?: string;
  /** A served loop: its length, and its loudness as encoded (−24 LUFS unless held under its peak). */
  readonly seconds?: number;
  readonly channels?: number;
  readonly sampleRate?: number;
  readonly lufs?: number;
  readonly truePeak?: number;
  readonly loop?: boolean;
};

export type Manifest = { readonly version: number; readonly files: readonly ManifestFile[] };

export const MANIFEST = data as unknown as Manifest;

/**
 * Where a stored file is served: `/worlds/` on the headset's own origin, or
 * `./worlds/` beside the page in a build with a relative base -- a demo
 * (`pnpm demo:build`) that has to work from whatever folder it is put in.
 */
export const worldUrl = (stored: string, base: string = import.meta.env.BASE_URL): string =>
  `${base}worlds/${stored}`;

/**
 * A texture's served file: `texture('gravel_ground_01', 'diff')`. Poly Haven
 * names most diffuse maps `_diff_` and a few `_diffuse_` (`forest_leaves_02`).
 */
export function textureFile(
  asset: string,
  map: 'diff' | 'nor_gl' | 'arm',
  manifest: Manifest = MANIFEST,
): ManifestFile {
  const names = map === 'diff' ? ['diff', 'diffuse'] : [map];
  const file = manifest.files.find(
    (candidate) =>
      candidate.served &&
      candidate.kind === 'texture' &&
      candidate.asset === asset &&
      names.some((name) => candidate.id.startsWith(`${asset}_${name}_`)),
  );
  if (!file) throw new Error(`no served ${map} texture for ${asset} in the worlds manifest`);
  return file;
}

/** A model's served (rewritten) glTF. */
export function modelFile(asset: string, manifest: Manifest = MANIFEST): ManifestFile {
  const file = manifest.files.find(
    (candidate) => candidate.served && candidate.kind === 'gltf' && candidate.asset === asset,
  );
  if (!file) throw new Error(`no served model ${asset} in the worlds manifest`);
  return file;
}

/** A sky's file. */
export function hdriFile(asset: string, manifest: Manifest = MANIFEST): ManifestFile {
  const file = manifest.files.find(
    (candidate) => candidate.kind === 'hdri' && candidate.asset === asset,
  );
  if (!file) throw new Error(`no sky ${asset} in the worlds manifest`);
  return file;
}

/** Every served file of a world, by the manifest. */
export function worldFiles(world: WorldSlug, manifest: Manifest = MANIFEST): ManifestFile[] {
  return manifest.files.filter((file) => file.served && file.worlds.includes(world));
}

/** A file by its stored name. */
export function byStored(stored: string, manifest: Manifest = MANIFEST): ManifestFile | undefined {
  return manifest.files.find((file) => file.stored === stored);
}
